import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { authApi } from "@/features/auth/services/authApi";
import { isRejectedSessionError, refreshAuthSession } from "@/shared/services/api";

/** Inactivity window before forced logout. Must match the backend
 *  JwtSettings.RefreshTokenInactivityMinutes so client and server agree. */
const IDLE_LIMIT_MS = 15 * 60_000;
/** How long before the limit the "stay logged in?" warning appears. */
const WARNING_LEAD_MS = 60_000;
/** Cadence of the idle/refresh check while the tab is visible. */
const CHECK_INTERVAL_MS = 5_000;
/** Minimum gap between localStorage activity writes (avoids write storms). */
const ACTIVITY_THROTTLE_MS = 5_000;
/** Refresh the access token this long before it expires (active users only). */
const REFRESH_LEAD_MS = 2 * 60_000;
/** Keep the server's 15-minute refresh-token idle window alive during active work. */
const ACTIVE_REFRESH_INTERVAL_MS = 5 * 60_000;

/** Shared across tabs so activity / logout in one tab is seen by the others. */
const LS_LAST_ACTIVITY = "lastActivityAt";
const LS_LOGGED_OUT = "sessionLoggedOut";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousedown",
  "keydown",
  "input",
  "change",
  "pointermove",
  "scroll",
  "touchstart",
  "click",
];

const now = () => Date.now();

const readLastActivity = (): number => {
  const raw = localStorage.getItem(LS_LAST_ACTIVITY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : now();
};

const writeLastActivity = (ts: number) => {
  localStorage.setItem(LS_LAST_ACTIVITY, String(ts));
};

export interface IdleTimeoutState {
  /** Whether the "session about to expire" warning should be shown. */
  showWarning: boolean;
  /** Whole seconds left until forced logout (only meaningful while warning). */
  secondsLeft: number;
  /** True while a token refresh triggered by "Stay logged in" is in flight. */
  isRefreshing: boolean;
  /** Reset the idle timer and refresh the token ("Stay logged in"). */
  stayLoggedIn: () => void;
  /** Revoke the session and redirect to /login now ("Log out"). */
  logoutNow: () => void;
}

/**
 * Tracks genuine user activity (mouse/keyboard/scroll/touch) and enforces a
 * 15-minute inactivity logout. Unlike the old 401-reactive flow, this measures
 * idle time from a timestamp, so it behaves correctly across backgrounded tabs:
 *
 *  - setInterval is throttled in hidden tabs, so we recompute `idleFor` from the
 *    stored timestamp on every tick AND on visibilitychange. If the user returns
 *    to an abandoned tab already past the limit, they are logged out immediately
 *    rather than offered a grace dialog.
 *  - Active users are kept logged in seamlessly: the access token is silently
 *    refreshed shortly before it expires, so the warning only ever appears for
 *    real inactivity.
 *  - lastActivityAt and a logout flag live in localStorage, so activity or logout
 *    in any tab is mirrored to all of them.
 *
 * Mount this once inside the authenticated shell (ProtectedLayout).
 */
export const useIdleTimeout = (): IdleTimeoutState => {
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARNING_LEAD_MS / 1000));
  const [isRefreshing, setIsRefreshing] = useState(false);

  const lastWriteRef = useRef(0);
  const silentRefreshingRef = useRef(false);
  const loggedOutRef = useRef(false);

  /** Persist activity, throttled. `force` bypasses the throttle (e.g. "Stay logged in"). */
  const recordActivity = useCallback((force = false) => {
    const ts = now();
    if (force || ts - lastWriteRef.current >= ACTIVITY_THROTTLE_MS) {
      lastWriteRef.current = ts;
      writeLastActivity(ts);
    }
  }, []);

  /** Refresh the access token in place. Used by both the silent scheduler and "Stay logged in". */
  const refreshSession = useCallback((): Promise<boolean> => refreshAuthSession(), []);

  const hardLogout = useCallback(
    (broadcast = true) => {
      if (loggedOutRef.current) return;
      loggedOutRef.current = true;

      const { refreshToken, logout } = useAuthStore.getState();
      if (refreshToken) {
        // Fire-and-forget: revoke the session server-side.
        authApi.logout(refreshToken).catch(() => {});
      }
      logout();
      if (broadcast) {
        // Notify other tabs (value must change to fire the storage event).
        localStorage.setItem(LS_LOGGED_OUT, String(now()));
      }
      setShowWarning(false);
      navigate("/login");
    },
    [navigate]
  );

  const stayLoggedIn = useCallback(() => {
    recordActivity(true);
    setShowWarning(false);
    setIsRefreshing(true);
    refreshSession()
      .then((ok) => {
        if (!ok) hardLogout();
      })
      .catch((error) => {
        if (isRejectedSessionError(error)) hardLogout();
        else toast.error("Could not refresh your session. Please try again.");
      })
      .finally(() => setIsRefreshing(false));
  }, [recordActivity, refreshSession, hardLogout]);

  // ── Main loop: activity listeners, idle/refresh check, cross-tab sync ──
  useEffect(() => {
    // Treat mount (login / reload) as activity so a stale timestamp from a prior
    // session can't trigger an instant logout.
    recordActivity(true);

    const onActivity = () => recordActivity();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );

    const maybeSilentRefresh = () => {
      if (silentRefreshingRef.current) return;
      const { expiresAt, refreshToken } = useAuthStore.getState();
      if (!refreshToken || !expiresAt) return;
      const msUntilExpiry = new Date(expiresAt).getTime() - now();
      const lastRefreshAt = Number(localStorage.getItem("lastSessionRefreshAt")) || 0;
      if (now() - lastRefreshAt >= ACTIVE_REFRESH_INTERVAL_MS ||
          (Number.isFinite(msUntilExpiry) && msUntilExpiry <= REFRESH_LEAD_MS)) {
        silentRefreshingRef.current = true;
        refreshSession()
          .catch((error) => {
            if (isRejectedSessionError(error)) hardLogout();
          })
          .finally(() => {
            silentRefreshingRef.current = false;
          });
      }
    };

    const check = () => {
      // Once logout starts, ensure the warning stays hidden (prevents race where dialog
      // renders between hardLogout's server revocation and the redirect to /login).
      if (loggedOutRef.current) {
        setShowWarning(false);
        return;
      }
      const idleFor = now() - readLastActivity();

      if (idleFor >= IDLE_LIMIT_MS) {
        hardLogout();
        return;
      }

      if (idleFor >= IDLE_LIMIT_MS - WARNING_LEAD_MS) {
        // Derive the countdown from the timestamp so background throttling can't desync it.
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idleFor) / 1000)));
        setShowWarning(true);
      } else {
        setShowWarning(false);
        // Only keep the session alive for users who are actually active.
        maybeSilentRefresh();
      }
    };

    check();
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);

    // Hidden tabs throttle timers, so re-check the instant the tab is shown again.
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Mirror activity / logout from other tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_LOGGED_OUT) {
        hardLogout(false); // another tab already revoked + broadcast
      } else if (e.key === LS_LAST_ACTIVITY) {
        check(); // activity elsewhere may clear our warning
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      window.clearInterval(interval);
    };
  }, [recordActivity, refreshSession, hardLogout]);

  return {
    showWarning,
    secondsLeft,
    isRefreshing,
    stayLoggedIn,
    logoutNow: () => hardLogout(),
  };
};
